import { useEffect, useState } from 'react';
import { AttachmentViewer, type AttachmentViewerSubject } from '@/components/attachments/AttachmentViewer';
import { Badge, Button, ConfirmDialog, Modal, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { pluralise } from '@/lib/format';
import type { EmailThread, EmailThreadDetail } from '@/lib/types';
import { AssignEmailThreadDialog } from './AssignEmailThreadDialog';
import { EmailThreadMessages } from './EmailThreadMessages';
import {
  DENIED,
  MATCH_STATUS_LABELS,
  MATCH_STATUS_TONES,
  triageErrorMessage,
} from './email-log-format';

/*
 * One email thread — GET /email-log/threads/:id — with the triage actions
 * (assign to a company, ignore) when the reader holds `email-log.triage`.
 * Mounted ONCE per page; `thread` null = closed.
 */

type DetailFile = EmailThreadDetail['messages'][number]['files'][number];

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; detail: EmailThreadDetail };

export function EmailThreadModal({
  thread,
  canTriage,
  onClose,
  onChanged,
}: {
  /** The list row that was opened; null when closed. */
  thread: EmailThread | null;
  canTriage: boolean;
  onClose: () => void;
  /** A triage action succeeded; the list should refetch. */
  onChanged: () => void;
}) {
  return thread === null ? null : (
    <OpenThread
      key={thread.id}
      thread={thread}
      canTriage={canTriage}
      onClose={onClose}
      onChanged={onChanged}
    />
  );
}

function OpenThread({
  thread,
  canTriage,
  onClose,
  onChanged,
}: {
  thread: EmailThread;
  canTriage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reload, setReload] = useState(0);
  const [assignOpen, setAssignOpen] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [ignoreError, setIgnoreError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<AttachmentViewerSubject | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getEmailThread(thread.id, signal)
      .then((res) => {
        if (!signal.aborted) setState({ status: 'ready', detail: res.data });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        let message = caught instanceof Error ? caught.message : 'Failed to load the thread';
        if (caught instanceof ApiError) {
          if (caught.status === 403) message = DENIED;
          else if (caught.status === 404) message = 'This thread no longer exists.';
        }
        setState({ status: 'error', message });
      });
    return () => controller.abort();
  }, [thread.id, reload]);

  const current: EmailThread = state.status === 'ready' ? state.detail : thread;
  const company = current.company;
  const canAssign = canTriage && current.matchStatus !== 'matched';
  const canIgnore =
    canTriage && (current.matchStatus === 'unmatched' || current.matchStatus === 'ambiguous');
  const subjectLabel = current.subject || '(no subject)';

  const openFile =
    company === null
      ? undefined
      : (file: DetailFile) => {
          const attachmentId = file.attachmentId;
          if (attachmentId === null) return;
          setViewer({
            kind: 'file',
            title: file.fileName,
            fileName: file.fileName,
            contentType: file.contentType,
            load: (signal) => api.downloadCompanyAttachment(company.id, attachmentId, signal),
          });
        };

  const confirmIgnore = () => {
    setIgnoring(true);
    setIgnoreError(null);
    api
      .ignoreEmailThread(current.id)
      .then(() => {
        setIgnoreOpen(false);
        onChanged();
        setReload((n) => n + 1);
      })
      .catch((caught: unknown) => {
        setIgnoreError(triageErrorMessage(caught, 'Failed to ignore the thread'));
      })
      .finally(() => setIgnoring(false));
  };

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={subjectLabel}
        size="lg"
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={MATCH_STATUS_TONES[current.matchStatus]} dot>
              {MATCH_STATUS_LABELS[current.matchStatus]}
            </Badge>
            <span>{pluralise(current.messageCount, 'message')}</span>
            {company && (
              <>
                <span aria-hidden="true">·</span>
                <TextLink to={`/companies/${company.id}`}>{company.name}</TextLink>
              </>
            )}
          </span>
        }
        footer={
          canAssign || canIgnore ? (
            <>
              {canIgnore && (
                <Button variant="danger" onClick={() => setIgnoreOpen(true)}>
                  Ignore
                </Button>
              )}
              {canAssign && (
                <Button variant="primary" onClick={() => setAssignOpen(true)}>
                  Assign to company
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {state.status === 'loading' && (
          <div className="grid gap-3" aria-busy="true">
            {Array.from({ length: 2 }, (_, i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        )}
        {state.status === 'error' && (
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load the thread.</>}>
            <span className="[overflow-wrap:anywhere]">{state.message}</span>
          </Notice>
        )}
        {state.status === 'ready' && (
          <div className="grid gap-3">
            {current.matchStatus === 'ambiguous' && current.candidateCompanies.length > 0 && (
              <p className="m-0 flex flex-wrap gap-x-2 text-xs text-fg-muted">
                <span>Candidates:</span>
                {current.candidateCompanies.map((candidate, index) => (
                  <span key={candidate.id}>
                    <TextLink to={`/companies/${candidate.id}`}>{candidate.name}</TextLink>
                    {index < current.candidateCompanies.length - 1 ? ',' : ''}
                  </span>
                ))}
              </p>
            )}
            <EmailThreadMessages messages={state.detail.messages} onOpenFile={openFile} />
          </div>
        )}
      </Modal>

      <AssignEmailThreadDialog
        thread={assignOpen ? current : null}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => {
          setAssignOpen(false);
          onChanged();
          setReload((n) => n + 1);
        }}
      />

      <ConfirmDialog
        open={ignoreOpen}
        onClose={() => {
          if (ignoring) return;
          setIgnoreOpen(false);
          setIgnoreError(null);
        }}
        onConfirm={confirmIgnore}
        title={`Ignore “${subjectLabel}”?`}
        description="The thread leaves the triage queue and is not saved to any company."
        confirmLabel="Ignore thread"
        busyLabel="Ignoring…"
        loading={ignoring}
        error={ignoreError}
      />

      <AttachmentViewer subject={viewer} onClose={() => setViewer(null)} />
    </>
  );
}
